import { Clock, Plus } from "lucide-react";
import { FABadge, isFAImported } from "@/components/app/FABadge";

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

// Collapse a pitch's 15-min slots into booked runs and free gaps (>= 30 min)
function segmentsFor(slots) {
  const out = [];
  let i = 0;
  while (i < slots.length) {
    const s = slots[i];
    let j = i + 1;
    if (s.booked && s.booking_id) {
      while (j < slots.length && slots[j].booking_id === s.booking_id) j++;
      out.push({ kind: "booked", s, start: s.minute, end: s.minute + (j - i) * 15, dim: s._dim });
    } else if (s.open && !s.booked) {
      while (j < slots.length && slots[j].open && !slots[j].booked) j++;
      const end = s.minute + (j - i) * 15;
      if (end - s.minute >= 30) out.push({ kind: "free", s, start: s.minute, end });
    } else {
      while (j < slots.length && !slots[j].open) j++;
    }
    i = j;
  }
  return out;
}

export function AgendaView({ rows, isAll, colorFor, refView, isMine = () => false, onOpenBooking, onBookSlot, canBook }) {
  let lastVenue = null;
  return (
    <div className="space-y-4" data-testid="venue-agenda">
      {rows.map(row => {
        const segs = segmentsFor(row.slots);
        const booked = segs.filter(x => x.kind === "booked").length;
        const showVenue = isAll && row.pitch.venue_name && row.pitch.venue_name !== lastVenue;
        lastVenue = row.pitch.venue_name || lastVenue;
        return (
          <div key={row.pitch.id} data-testid={`agenda-pitch-${row.pitch.id}`}>
            {showVenue && <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mt-2 mb-1">{row.pitch.venue_name}</div>}
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="font-display font-bold text-sm tracking-tight">{row.pitch.name}</div>
              <div className="text-[10px] tracking-widest uppercase opacity-50">{row.pitch.game_format || row.pitch.surface}{segs.length === 0 ? " · closed" : booked ? ` · ${booked} booked` : " · free all day"}</div>
            </div>
            <div className="space-y-1.5">
              {segs.map(seg => seg.kind === "booked" ? (
                <button key={`${row.pitch.id}-${seg.start}`} type="button" onClick={() => onOpenBooking(seg.s.booking_id)} data-testid={`agenda-booking-${seg.s.booking_id}`}
                  className={`w-full text-left rounded-md border px-3 py-2.5 flex items-center gap-3 active:brightness-110 ${colorFor(seg.s).bg} ${colorFor(seg.s).text} ${colorFor(seg.s).border} ${seg.dim ? "opacity-30" : ""} ${isMine(seg.s) ? "booking-pulse" : ""}`}>
                  <div className="font-display font-black text-sm tabular-nums leading-tight w-[4.6rem] flex-shrink-0">
                    {hhmm(seg.start)}<div className="text-[10px] opacity-70 font-normal">– {hhmm(seg.end)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{seg.s.age_group ? `${seg.s.age_group} ` : ""}{seg.s.team_name || seg.s.match_type || "Booked"}{seg.s.opponent ? ` vs ${seg.s.opponent}` : ""}</div>
                    {refView && seg.s.session_type === "game" && (
                      <div data-testid={`agenda-ref-${seg.s.booking_id}`} className={`text-[11px] font-bold ${seg.s.referee_name ? "" : "text-amber-300"}`}>{seg.s.referee_name ? `Ref: ${seg.s.referee_name}${seg.s.referee_actual_name ? ` (${seg.s.referee_actual_name})` : ""}` : seg.s.referee_tbd ? "Ref: TBA" : "Ref: none"}{seg.s.referee_name && seg.s.referee_fee != null && <span className="text-[#83B68B] ml-1.5" data-testid={`agenda-ref-fee-${seg.s.booking_id}`}>· £{Number(seg.s.referee_fee).toFixed(2)}</span>}</div>
                    )}
                    <div className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-2 flex-wrap">
                      {seg.s.match_type && <span>{seg.s.match_type}</span>}
                      {seg.s.session_type && <span>{seg.s.session_type}</span>}
                      {seg.s.shared_count > 0 && <span>+{seg.s.shared_count} sharing</span>}
                      {seg.s.confirmation_status === "awaiting" && <span className="text-amber-200">awaiting venue</span>}
                    </div>
                  </div>
                  {isFAImported(seg.s) && <FABadge variant="solid" size="xs" className="flex-shrink-0" />}
                </button>
              ) : (
                <button key={`${row.pitch.id}-${seg.start}`} type="button" disabled={!canBook} onClick={() => onBookSlot(row.pitch, seg.s)} data-testid={`agenda-free-${row.pitch.id}-${seg.start}`}
                  className="w-full text-left rounded-md border border-dashed border-black/20 dark:border-white/15 px-3 py-2 flex items-center gap-3 text-xs opacity-70 hover:opacity-100 hover:border-[#83B68B] disabled:opacity-40">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="tabular-nums">{hhmm(seg.start)} – {hhmm(seg.end)}</span>
                  <span className="opacity-60">free · {(seg.end - seg.start) >= 60 ? `${Math.floor((seg.end - seg.start) / 60)}h${(seg.end - seg.start) % 60 ? ` ${(seg.end - seg.start) % 60}m` : ""}` : `${seg.end - seg.start} min`}</span>
                  {canBook && <span className="ml-auto inline-flex items-center gap-1 text-[#83B68B] text-[10px] tracking-widest uppercase"><Plus className="w-3 h-3" /> Book</span>}
                </button>
              ))}
              {segs.length === 0 && <div className="text-xs opacity-40 px-1">Closed</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
